// OneDrive:IgnoreCodeCoverage

import { createPairedEvent } from '../PairedEventBase';
import { IEventType } from '../EventBase';
import { IEvent, EventFieldType } from '../IEvent';
import Promise from '../../async/Promise';
import { Qos, IQosStartSchema, IQosEndSchema } from './Qos.event';
import { ResultTypeEnum } from './ResultTypeEnum';
export { ResultTypeEnum };
export interface IApiStartSchema extends IQosStartSchema {
    /**
     * The api url
     */
    url: string;
}

export interface IApiEndSchema extends IQosEndSchema {
    /**
     * The api url
     */
    url?: string;
}

export interface IApi extends IEvent {
    data: IApiStartSchema & IApiEndSchema;
    /**
     * This writes verbose logs to memory to be uploaded if event ends with failure.
     */
    verbose(message: string): void;
    /**
     * Sets a time limit for the scenario. A timeout result will be logged if no end happens in desired timeframe.
     */
    setTimeout(ms: number, data: IApiEndSchema): void;

    /**
     * This will end the Api event
     * @param data {IApiEndSchema}     * -optional startTimeOverride - This is a startTime override for the qos event
     * -optional resultCode - This is result code for the qos event
     * -required resultType - This is the result type enum
     * -optional error - This is the error message to be logged with failed result
     * -optional extraData - This is the random extra data for the qos event
     * -optional extraMetrics - Extra data for the qos event that should be logged as aggregatable metrics
     */
    end(data: IApiEndSchema): void;
}

export type Api = IApi;

export const Api: IApiType = createPairedEvent<IApiStartSchema, IApiEndSchema>({
    eventName: 'Api,Qos,',
    shortEventName: 'Api'
}, {
    url: EventFieldType.String
}, Qos);

export interface IApiType extends IEventType<IApiStartSchema & IApiEndSchema> {
    prototype: Api;
    /**
     * The Api event constructor
     * @param data {IApiStartSchema}     * -required url - The api url
     * -required name - This is the name of the qos event
     * -optional startTimeOverride - This is a startTime override for the qos event
     * -optional resultCode - This is result code for the qos event
     * -optional resultType - This is the result type enum
     * -optional error - This is the error message to be logged with failed result
     * -optional extraData - This is the random extra data for the qos event
     * -optional extraMetrics - Extra data for the qos event that should be logged as aggregatable metrics
     * @param parent {IEvent} The optional parent for this event
     */
    new (data: IApiStartSchema, parent?: IEvent): Api;

    instrumentPromise<T>(
        this: IApiType,
        startSchema: IApiStartSchema,
        createPromise: () => Promise<T>,
        getCompleteSchema?: (result?: T) => IApiEndSchema,
        getErrorSchema?: (error?: any) => IApiEndSchema,
        timeoutMs?: number,
        timeoutSchema?: IApiEndSchema,
        parent?: IEvent): Promise<T>;
}

export default Api;
